import { useEffect, useMemo, useState } from "react";
import { Search, Pencil, Trash2, Printer, X, Save } from "lucide-react";
import { toast } from "sonner";
import { Card } from "./ui/card";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "./ui/alert-dialog";
import { Label } from "./ui/label";
import { FieldGrade, FieldSection, FieldAcademicYear, FieldEnrollmentStatus } from "./StudentFormFields";
import { GRADES, STAGES, STAGE_KEYS, can, sectionsForGrade } from "../lib/constants";
import { useAuth } from "../contexts/AuthContext";
import { printHtmlReport } from "../lib/pdf";

export default function StudentTable({
  title, rows, columns, dateKey, dateLabel,
  apiUpdate, apiDelete, reload, testidPrefix, accent = "teal",
  showEnrollmentStatus = false,
}) {
  const { user } = useAuth();
  const canEdit = can.editStudent(user);
  const canDelete = can.deleteStudent(user);

  const [search, setSearch] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [gradeFilter, setGradeFilter] = useState("all");
  const [sectionFilter, setSectionFilter] = useState("all");
  const [enrollmentFilter, setEnrollmentFilter] = useState("all");
  const [editRow, setEditRow] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (editRow) setEditForm({ ...editRow }); }, [editRow]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(r => {
      const matchSearch = !q || r.name?.toLowerCase().includes(q) || (r.national_id || "").toLowerCase().includes(q);
      const matchStage = stageFilter === "all" || r.stage === stageFilter;
      const matchGrade = gradeFilter === "all" || r.grade === gradeFilter;
      const matchSection = sectionFilter === "all" || r.section === sectionFilter;
      const matchEnrollment = !showEnrollmentStatus || enrollmentFilter === "all" || r.enrollment_status === enrollmentFilter;
      return matchSearch && matchStage && matchGrade && matchSection && matchEnrollment;
    });
  }, [rows, search, stageFilter, gradeFilter, sectionFilter, enrollmentFilter, showEnrollmentStatus]);

  const handlePrint = () => {
    printHtmlReport({
      title,
      columns: [
        { key: "name", header: "اسم الطالب" },
        { key: "national_id", header: "رقم الهوية" },
        ...(columns.find(c => c.key === "phone") ? [{ key: "phone", header: "رقم الجوال" }] : []),
        { key: "grade", header: "الصف" },
        { key: "section", header: "الفصل" },
        { key: "academic_year", header: "العام الدراسي" },
        { key: dateKey, header: dateLabel },
        ...(showEnrollmentStatus ? [{ key: "enrollment_status", header: "الحالة" }] : []),
      ],
      rows: filtered,
      meta: [
        { label: "البحث", value: search || "—" },
        { label: "المرحلة", value: stageFilter === "all" ? "الكل" : STAGES[stageFilter] },
        { label: "الصف", value: gradeFilter === "all" ? "الكل" : gradeFilter },
        ...(showEnrollmentStatus ? [{ label: "الحالة", value: enrollmentFilter === "all" ? "الكل" : enrollmentFilter }] : []),
      ]
    });
  };

  const onSaveEdit = async () => {
    if (editForm.national_id && !/^[0-9]{8,15}$/.test(editForm.national_id)) {
      toast.error("رقم الهوية غير صالح (اتركه فارغاً أو 8-15 رقم)");
      return;
    }
    setSaving(true);
    try {
      await apiUpdate(editForm.id, editForm);
      toast.success("تم تحديث البيانات بنجاح");
      setEditRow(null);
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذر حفظ التعديلات");
    } finally {
      setSaving(false);
    }
  };

  const onConfirmDelete = async () => {
    try {
      await apiDelete(deleteRow.id);
      toast.success("تم حذف السجل");
      setDeleteRow(null);
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذر الحذف");
    }
  };

  const accentBtn = accent === "rose" ? "bg-rose-600 hover:bg-rose-700" : "bg-teal-700 hover:bg-teal-800";

  return (
    <Card className="border-slate-200 overflow-hidden">
      {/* Toolbar */}
      <div className="p-3 sm:p-5 border-b border-slate-100 bg-slate-50/60 flex flex-wrap gap-2 sm:gap-3 items-center">
        <div className="relative flex-1 min-w-[180px] sm:min-w-[200px]">
          <Search size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="ابحث بالاسم أو رقم الهوية..." value={search} onChange={(e) => setSearch(e.target.value)} className="pr-9 bg-white h-10 text-sm" dir="rtl" data-testid={`${testidPrefix}-search`} />
        </div>
        <Select value={stageFilter} onValueChange={setStageFilter}>
          <SelectTrigger className="bg-white h-10 w-full sm:w-[180px] text-sm" dir="rtl" data-testid={`${testidPrefix}-stage-filter`}><SelectValue /></SelectTrigger>
          <SelectContent dir="rtl">
            <SelectItem value="all">جميع المراحل</SelectItem>
            {STAGE_KEYS.map(k => <SelectItem key={k} value={k}>{STAGES[k]}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={gradeFilter} onValueChange={(v) => { setGradeFilter(v); setSectionFilter("all"); }}>
          <SelectTrigger className="bg-white h-10 w-full sm:w-[200px] text-sm" dir="rtl" data-testid={`${testidPrefix}-grade-filter`}><SelectValue /></SelectTrigger>
          <SelectContent dir="rtl">
            <SelectItem value="all">جميع الصفوف</SelectItem>
            {GRADES.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={sectionFilter} onValueChange={setSectionFilter} disabled={gradeFilter === "all"}>
          <SelectTrigger className="bg-white h-10 w-full sm:w-[160px] text-sm" dir="rtl" data-testid={`${testidPrefix}-section-filter`}><SelectValue /></SelectTrigger>
          <SelectContent dir="rtl">
            <SelectItem value="all">جميع الفصول</SelectItem>
            {sectionsForGrade(gradeFilter === "all" ? "" : gradeFilter).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        {showEnrollmentStatus && (
          <Select value={enrollmentFilter} onValueChange={setEnrollmentFilter}>
            <SelectTrigger className="bg-white h-10 w-full sm:w-[160px] text-sm" dir="rtl" data-testid={`${testidPrefix}-enrollment-filter`}><SelectValue /></SelectTrigger>
            <SelectContent dir="rtl">
              <SelectItem value="all">الكل</SelectItem>
              <SelectItem value="مستجد">مستجد</SelectItem>
              <SelectItem value="مستمر">مستمر</SelectItem>
            </SelectContent>
          </Select>
        )}
        <Button onClick={handlePrint} className={`h-10 gap-2 text-white ${accentBtn} btn-elevate w-full sm:w-auto`} data-testid={`${testidPrefix}-print`}>
          <Printer size={16} /> طباعة / PDF
        </Button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto -mx-px">
        <table className="w-full text-xs sm:text-sm min-w-[720px]" data-testid={`${testidPrefix}-table`}>
          <thead>
            <tr className="bg-teal-700 text-white">
              <th className="px-2 sm:px-3 py-2.5 sm:py-3 text-center font-cairo font-semibold w-12">#</th>
              {columns.map(c => (
                <th key={c.key} className="px-2 sm:px-3 py-2.5 sm:py-3 text-right font-cairo font-semibold whitespace-nowrap">{c.header}</th>
              ))}
              {(canEdit || canDelete) && <th className="px-2 sm:px-3 py-2.5 sm:py-3 text-center font-cairo font-semibold w-24 sm:w-32">الإجراءات</th>}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={columns.length + ((canEdit || canDelete) ? 2 : 1)} className="px-3 py-12 text-center text-slate-400">لا توجد سجلات للعرض</td></tr>
            ) : filtered.map((r, idx) => (
              <tr key={r.id} className="odd:bg-white even:bg-slate-50/60 hover:bg-teal-50/50">
                <td className="px-2 sm:px-3 py-2 sm:py-2.5 text-center text-slate-500 tabular-nums">{idx + 1}</td>
                {columns.map(c => (
                  <td key={c.key} className="px-2 sm:px-3 py-2 sm:py-2.5 text-slate-800 whitespace-nowrap">
                    {c.key === "stage" ? (STAGES[r.stage] || "—") : (r[c.key] || "—")}
                  </td>
                ))}
                {(canEdit || canDelete) && (
                  <td className="px-2 sm:px-3 py-2 sm:py-2.5">
                    <div className="flex items-center justify-center gap-1">
                      {canEdit && (
                        <Button size="sm" variant="ghost" onClick={() => setEditRow(r)} className="h-8 w-8 p-0 text-teal-700 hover:bg-teal-100" data-testid={`${testidPrefix}-edit-${r.id}`}>
                          <Pencil size={15} />
                        </Button>
                      )}
                      {canDelete && (
                        <Button size="sm" variant="ghost" onClick={() => setDeleteRow(r)} className="h-8 w-8 p-0 text-rose-600 hover:bg-rose-100" data-testid={`${testidPrefix}-delete-${r.id}`}>
                          <Trash2 size={15} />
                        </Button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="px-4 py-3 border-t border-slate-100 text-xs text-slate-500 bg-slate-50/60">
        إجمالي السجلات: <span className="font-bold text-slate-700 tabular-nums">{filtered.length}</span> من أصل <span className="tabular-nums">{rows.length}</span>
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editRow} onOpenChange={(o) => !o && setEditRow(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" dir="rtl">
          <DialogHeader><DialogTitle className="font-cairo text-lg sm:text-xl">تعديل بيانات الطالب</DialogTitle></DialogHeader>
          {editRow && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>اسم الطالب</Label>
                <Input value={editForm.name || ""} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="h-11" />
              </div>
              <div className="space-y-1.5">
                <Label>رقم الهوية</Label>
                <Input value={editForm.national_id || ""} onChange={(e) => setEditForm({ ...editForm, national_id: e.target.value })} className="h-11" />
              </div>
              {columns.find(c => c.key === "phone") && (
                <div className="space-y-1.5">
                  <Label>رقم الجوال</Label>
                  <Input value={editForm.phone || ""} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} className="h-11" />
                </div>
              )}
              <FieldGrade value={editForm.grade || ""} onChange={(v) => setEditForm({ ...editForm, grade: v, section: "" })} testid="edit-grade-select" />
              <FieldSection grade={editForm.grade || ""} value={editForm.section || ""} onChange={(v) => setEditForm({ ...editForm, section: v })} testid="edit-section-select" />
              <FieldAcademicYear value={editForm.academic_year || ""} onChange={(v) => setEditForm({ ...editForm, academic_year: v })} testid="edit-academic-year-select" />
              <div className="space-y-1.5">
                <Label>{dateLabel}</Label>
                <Input type="date" value={editForm[dateKey] || ""} onChange={(e) => setEditForm({ ...editForm, [dateKey]: e.target.value })} className="h-11" />
              </div>
              {showEnrollmentStatus && (
                <FieldEnrollmentStatus value={editForm.enrollment_status || "مستجد"} onChange={(v) => setEditForm({ ...editForm, enrollment_status: v })} testid="edit-enrollment-status-select" />
              )}
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setEditRow(null)} className="gap-1"><X size={16} /> إلغاء</Button>
            <Button onClick={onSaveEdit} disabled={saving} className={`gap-1 text-white ${accentBtn}`} data-testid="btn-save-edit">
              <Save size={16} /> {saving ? "جاري الحفظ..." : "حفظ التعديلات"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteRow} onOpenChange={(o) => !o && setDeleteRow(null)}>
        <AlertDialogContent dir="rtl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-cairo">تأكيد الحذف</AlertDialogTitle>
            <AlertDialogDescription>هل أنت متأكد من حذف سجل الطالب <span className="font-bold text-slate-800">{deleteRow?.name}</span>؟ لا يمكن التراجع عن هذا الإجراء.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>إلغاء</AlertDialogCancel>
            <AlertDialogAction onClick={onConfirmDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="btn-confirm-delete">تأكيد الحذف</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
